import { Formiz, useForm } from "@formiz/core";
import {
  navigateRoute,
  NotificationType,
  useApp,
  useAppDispatch,
  useAppSelector,
  useSubscriptionInfo,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Spinner } from "@nextui-org/react";
import localSession from "core-js/internals/task.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { PageLayout, PageSize } from "@/components";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import PageMetaTags from "@/components/common/PageMetaTags";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import useApi from "@/components/useApi";
import { ALL_EXTERNAL_PERMISSIONS, DOMAINS, ROUTES } from "@/constants";
import useManageSubscription from "@/hooks/useManageSubscription";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";
import { isUsernameValid } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import { IUpdateProfilePayload, ProfileFormFields } from "../types";
import { AdvancedSettings, UserProfileFooter, UserProfileInfo, YourBenefits } from "./components";
import AccountDeleteModal from "./components/AccountDeleteModal";
import GeneralSettingsSection from "./components/GeneralSettingsSection/GeneralSettingsSection";

const userProfileLogger = appLogger.withTag("UserProfile");
const DEBOUNCE_DELAY_MS = 500;
const MIN_USERNAME_LENGTH = 3;

const UserProfile = () => {
  const { t } = useTranslation();
  const { onUserSetBlurNSFW, showUnsavedProfileChangesWarning, setToShowUnsavedProfileChangesWarning, blurNSFW } = useUserSettings();
  const profileForm = useForm();
  const {
    onUserUpdateProfileV2,
    isLoading: isLoadingUpdate,
    errorMessage,
    role: subscriptionRole,
    resetProfile,
    setIsUserProfileModified,
    userProfile: profile,
    isProfileModified: hasChangesPending,
  } = useUserInfo();
  const { logout, permissions, isAuthenticated } = useAuth();
  const { jsonApiService, callApi: callApiCheckUsername } = useApi();
  const { callApi: callApiDelete, isLoading: isLoadingDelete } = useApi();
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);
  const [notAvailableUsername, setNotAvailableUsername] = useState("");
  const [permissionsToDisplay, setPermissionsToDisplay] = useState<string[]>([]);
  const { setFieldsValues, invalidateFields } = profileForm;
  const [debouncedUsername] = useDebounce(profileForm.values?.username, DEBOUNCE_DELAY_MS);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isPartner = getIsPartner();
  const { logoutDomain = DOMAINS.SPICYCHAT } = useApp();
  const path = useAppSelector((state) => state?.navigation?.path) || "";
  const initialValues = useMemo(
    () => ({
      username: profile?.username?.toLowerCase(),
      name: profile?.name,
      avatar_url: profile?.avatar_url ?? null,
      openai_api_key: profile?.openai_api_key ?? "",
      openai_mode: profile?.openai_mode ?? null,
      highlights: profile?.highlights || "",
    }),
    [profile],
  );
  const { displayToast } = useToast();
  const { shouldWarnOnAccountDelete } = useSubscriptionInfo();
  const { manageLinkHref, isExternalManageLink, isMobileManagedSubscription } = useManageSubscription();

  const handleBlurNsfwToggle = useCallback(async () => {
    await onUserSetBlurNSFW(!blurNSFW);
  }, [onUserSetBlurNSFW, blurNSFW]);

  useEffect(() => {
    return () => setIsUserProfileModified(false);
  }, [dispatch]);

  useEffect(() => {
    const checkUsernameAvailability = async (username: string) => {
      try {
        await callApiCheckUsername(jsonApiService.checkUsername, username).then((response) => {
          const { isAvailable } = response as { isAvailable: boolean };
          if (!isAvailable && username !== profile?.username?.toLowerCase()) {
            invalidateFields({ username: t("user:profile.validation.username.notAvailable") });
            setNotAvailableUsername(username);
          }
          setIsUsernameAvailable(isAvailable);
          setIsCheckingUsername(false);
        });
      } catch (error) {
        userProfileLogger.log(error);
      }
    };
    if (
      profile?.username !== debouncedUsername &&
      debouncedUsername !== notAvailableUsername &&
      isUsernameValid(debouncedUsername) &&
      debouncedUsername &&
      debouncedUsername.length >= MIN_USERNAME_LENGTH
    ) {
      setIsCheckingUsername(true);
      setNotAvailableUsername("");
      checkUsernameAvailability(debouncedUsername);
    }
  }, [debouncedUsername, callApiCheckUsername, jsonApiService.checkUsername, invalidateFields, profile?.username]);

  const isUsernameFieldValid = profileForm.fields[ProfileFormFields.USERNAME]?.isValid;
  useEffect(() => {
    // if previously username already validated and not available,then show error
    if (profileForm?.values?.username && profileForm?.values?.username === notAvailableUsername) {
      invalidateFields({ username: t("user:profile.validation.username.notAvailable") });
      setIsUsernameAvailable(false);
    } else {
      setIsUsernameAvailable(null);
    }
  }, [profileForm?.values?.username, isUsernameFieldValid]);

  useEffect(() => {
    if (permissions) {
      const finalPerm = [] as string[];
      permissions?.map((permission) => {
        if (!ALL_EXTERNAL_PERMISSIONS.includes(permission)) {
          finalPerm.push(permission);
        }
      });
      setPermissionsToDisplay(finalPerm);
    }
  }, [permissions]);

  const handleOnClose = useCallback(() => {
    setToShowUnsavedProfileChangesWarning(false);
    dispatch(navigateRoute(""));
  }, [setToShowUnsavedProfileChangesWarning, dispatch]);

  const handleDiscard = useCallback(() => {
    setToShowUnsavedProfileChangesWarning(false);
    navigate(path);
    dispatch(navigateRoute(""));
  }, [setToShowUnsavedProfileChangesWarning, navigate, path, dispatch]);

  const logoutAndGoToHome = useCallback(async () => {
    await logout();
    localSession.clear();
    if (logoutDomain && isPartner) {
      window.open(`https://${logoutDomain}`, "_self");
    } else {
      navigate("/");
    }
  }, [logout, logoutDomain, isPartner, navigate]);

  const deleteUser = useCallback(async () => {
    const result = await callApiDelete(jsonApiService.deleteResource, "users");
    if (result) {
      resetProfile();
      await logoutAndGoToHome();
    }
  }, [callApiDelete, jsonApiService.deleteResource, resetProfile, logoutAndGoToHome]);

  const submitForm = useCallback(async () => {
    const formValues = profileForm.values;
    const payload: IUpdateProfilePayload = {
      username: formValues?.username,
      name: formValues?.name,
      highlights: formValues?.highlights,
    };

    if (profileForm?.values?.avatar_url) {
      payload.avatar_url = formValues?.avatar_url;
    }

    const { error } = await onUserUpdateProfileV2(payload);
    if (error) {
      setToShowUnsavedProfileChangesWarning(false);
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
      return;
    }
    setIsUsernameAvailable(null);
    setIsUserProfileModified(false);
    if (path !== "") {
      navigate(path);
    }
    displayToast({ message: t("user:profile.toast.saved"), type: NotificationType.success });
    dispatch(navigateRoute(""));
  }, [profileForm.values, onUserUpdateProfileV2, setToShowUnsavedProfileChangesWarning, displayToast, t, setIsUserProfileModified, path, navigate, dispatch]);

  const handleConfirm = useCallback(async () => {
    await submitForm();
  }, [submitForm]);

  const handleCloseDeleteDialog = useCallback(() => {
    setShowDeleteConfirmationDialog(false);
  }, []);

  useEffect(() => {
    const formFields = ["name", "username", "avatar_url", "openai_api_key", "openai_mode", "highlights"];

    const hasChanges = formFields.some(
      (field) => profileForm.values[field] !== undefined && profileForm.values[field] !== initialValues[field as keyof typeof initialValues],
    );

    if (hasChanges) {
      setIsUserProfileModified(true);
    } else {
      setIsUserProfileModified(false);
    }
  }, [profileForm, initialValues, dispatch]);

  useEffect(() => {
    const backNavigate = () => {
      if (hasChangesPending) {
        setToShowUnsavedProfileChangesWarning(true);
        window.history.pushState(null, "", window.location.href);
        dispatch(navigateRoute("/"));
      } else {
        dispatch(navigateRoute(""));
        navigate("/");
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", backNavigate);

    return () => {
      window.removeEventListener("popstate", backNavigate);
    };
  }, [hasChangesPending]);

  if (!profile) {
    return (
      <>
        {!isAuthenticated && <PageMetaTags canonicalUrl={ROUTES.login} prerenderStatusCode={401} />}
        <Spinner />
      </>
    );
  }

  return (
    <PageLayout size={PageSize.Medium} className="rounded-[16px] bg-gray-1 dark:bg-gray-3 p-lg">
      <PageStaticMetaTags pageId="userProfile" />
      <PageLayout.Header title={t("user:profile.title")} />
      <Formiz onValidSubmit={submitForm} connect={profileForm} initialValues={initialValues}>
        <form onSubmit={profileForm.submit}>
          <UserProfileInfo
            profileForm={profileForm}
            setFieldsValues={setFieldsValues}
            isLoadingUpdate={isLoadingUpdate}
            isCheckingUsername={isCheckingUsername}
            isUsernameAvailable={isUsernameAvailable}
            errorMessage={errorMessage}
            debouncedUsername={debouncedUsername}
          />
          <GeneralSettingsSection handleBlurNsfwToggle={handleBlurNsfwToggle} />
          {!isPartner && !subscriptionRole && permissionsToDisplay?.length > 1 && <YourBenefits permissionsToDisplay={permissionsToDisplay} />}
          <AdvancedSettings
            setShowDeleteConfirmationDialog={setShowDeleteConfirmationDialog}
            isLoadingDelete={isLoadingDelete}
            profileForm={profileForm}
          />

          <div className="py-sm">
            <hr className=" w-full h-[1px] bg-gray-10" />
          </div>
          <UserProfileFooter logoutAndGoToHome={logoutAndGoToHome} />
        </form>
      </Formiz>
      <ConfirmationModal
        heading={t("user:profile.modal.unsavedChanges.title")}
        handleClose={handleDiscard}
        onUserConfirmation={handleConfirm}
        handleOnClose={handleOnClose}
        confirmationMessage={t("user:profile.modal.unsavedChanges.message")}
        isVisible={showUnsavedProfileChangesWarning}
        actionButtonText={t("common:update")}
        discardButtonText={t("common:discard")}
        isLoading={isLoadingUpdate}
        screen="UserProfile"
      />
      {showDeleteConfirmationDialog ? (
        <AccountDeleteModal
          isVisible
          onClose={handleCloseDeleteDialog}
          isLoading={isLoadingDelete}
          onConfirmation={deleteUser}
          isManageSubscription={shouldWarnOnAccountDelete}
          isMobileManagedSubscription={isMobileManagedSubscription}
          manageLinkHref={manageLinkHref}
          isExternalManageLink={isExternalManageLink}
        />
      ) : null}
    </PageLayout>
  );
};

export default UserProfile;
